// HTTP boundary for every call this app makes to its backend.
//
//   apiFetch(path, init)   — raw Response. Throws UnauthorizedError on 401.
//   apiJson<T>(path, init) — parsed JSON. Throws UnauthorizedError on 401,
//                            ApiError on any other non-2xx.
//
// Both helpers:
//   - prepend VITE_API_BASE_URL (default `/api`; CloudFront forwards
//     that prefix to the backend in every deploy env)
//   - forward the cached zoid token as `Authorization: Bearer …` so the
//     backend can act as the current user against general-api
//   - send `credentials: "include"` so the HttpOnly `authToken` cookie
//     roundtrips
//
// 401 is special-cased because it always means the same thing app-wide
// (session dead, re-auth required) — every feature would otherwise repeat
// the same check. Anything else is surfaced as ApiError so callers can
// switch on `.status` / `.body` for feature-specific handling.

import { ApiError, UnauthorizedError } from "./errors";
import { getSessionToken } from "./sessionToken";

// `||` (not `??`) so an explicitly empty `VITE_API_BASE_URL=` in .env
// also falls back to `/api` — only an override with a real value should
// change the prefix.
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

export interface ApiFetchInit extends RequestInit {
  signal?: AbortSignal;
}

export async function apiFetch(path: string, init: ApiFetchInit = {}): Promise<Response> {
  const token = await getSessionToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (response.status === 401) {
    throw new UnauthorizedError();
  }
  return response;
}

export async function apiJson<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const response = await apiFetch(path, init);
  if (!response.ok) {
    // Best-effort: try to surface the backend's `{ error: "..." }` body to
    // the caller. If the response isn't JSON, body stays null.
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      /* non-JSON body — ignore */
    }
    throw new ApiError(response.status, body);
  }
  return response.json() as Promise<T>;
}
